import type {ReactElement} from 'react';
import React, {useMemo, useRef, useState} from 'react';
import {Icon} from '@Components/icon-v2';
import {IconShape, IconSize, IconType} from '@Components/icon-v2/icon.types';
import type {EmojiClickData} from 'emoji-picker-react';
import EmojiPicker from 'emoji-picker-react';
import type {ALIGNMENT_TYPE} from '@Components/dropdown-v2/dropdown.types';
import styles from './emoji-picker-input.module.scss';
import useOutsideClick from "@Hooks/useOutsideClick";

interface EmojiPickerInputProps {
  textAreaRef: React.RefObject<HTMLTextAreaElement | null>;
  onEmojiSelectCallback: (textWithEmoji: string) => void;
  alignment?: ALIGNMENT_TYPE;
}

const EMOJI_PICKER_HEIGHT = 400;
const EMOJI_PICKER_WIDTH = 280;

export function EmojiPickerInput({textAreaRef, ...props}: EmojiPickerInputProps): ReactElement<any> {
  const [isEmojiSelectorVisible, setIsEmojiSelectorVisible] = useState(false);
  const iconRef = useRef<HTMLElement>(null);
  const emojiPickerRef = useRef<HTMLDivElement>(null);
  const emojiPickerContainerRef = useRef<HTMLDivElement | null>(null);
  useOutsideClick(emojiPickerContainerRef, (): void => {
    setIsEmojiSelectorVisible(false)
  })

  const onEmojiSelected = (emojiData: EmojiClickData): void => {
    if (textAreaRef.current) {
      const emoji = String.fromCodePoint(parseInt(emojiData.unified, 16));
      const textArea = textAreaRef.current;
      const {selectionStart, selectionEnd} = textArea;

      const textBefore = textArea.value.substring(0, selectionStart);
      const textAfter = textArea.value.substring(selectionEnd);

      const newCaption = `${textBefore}${emoji}${textAfter}`;

      // eslint-disable-next-line react-compiler/react-compiler
      textArea.value = newCaption;

      const newCursorPosition = selectionStart + emoji.length;
      textArea.selectionStart = textArea.selectionEnd = newCursorPosition;

      props.onEmojiSelectCallback(newCaption);
    }
  };

  const onEmojiSelectorClick = (): void => {
    setIsEmojiSelectorVisible(!isEmojiSelectorVisible);
  };

  const emojiPicker = useMemo((): ReactElement<any> | null => {
    if (!isEmojiSelectorVisible) {
      return null;
    }

    return (
      <div ref={emojiPickerRef} className={`${styles.emojiSelector} ${isEmojiSelectorVisible ? styles.visible : ''}`}>
        <EmojiPicker width={EMOJI_PICKER_WIDTH} height={EMOJI_PICKER_HEIGHT} onEmojiClick={onEmojiSelected} lazyLoadEmojis/>
      </div>
    );
  }, [isEmojiSelectorVisible]);

  return (
    <div ref={emojiPickerContainerRef} className={styles.emojiPickerInputContainer}>
      <Icon
        ref={iconRef}
        isSelected={isEmojiSelectorVisible}
        className={styles.emojiPickerIcon}
        icon="icon-sentiment-very-satisfied"
        type={IconType.GHOST}
        shape={IconShape.SQUARE}
        size={IconSize.SIZE_ICON_20}
        onClick={onEmojiSelectorClick}
      />
      {emojiPicker}
    </div>
  );
}
